import { MediaQuality, MediaQualityEnum, default as Upload } from "../upload/Upload"

export interface Range {
  start: number,
  end: number
}

export interface UploadGroupData {
  lastIndex: number,
  uploads: Upload[],
  ranges: Range[]
}

export function mergeUploads(currentUploads: Upload[], newUploads: Upload[], limit: number): Upload[] {
  let set: any = {}

  for (let upload of currentUploads) {
    set[upload.code] = upload
  }

  for (let upload of newUploads) {
    set[upload.code] = upload
  }

  let array: Upload[] = Object.values(set)
  array.sort((a, b) => { return b.createdAt - a.createdAt })

  if (array.length > limit) {
    array = array.slice(0, limit)
  }

  return array
}

export function getUploadGroupData(fromIndex: number, groupSize: number, source: Upload[]): UploadGroupData {
  let ranges = generateRanges(fromIndex, groupSize, source.length)
  let subgroups = getSubgroups(ranges, source)

  if (subgroups.flat().length !== groupSize) {
    throw new Error("Group size incorrect")
  }

  return {
    lastIndex: ranges[ranges.length - 1].end,
    uploads: subgroups.flat(),
    ranges: ranges
  }
}

export function sumRanges(ranges: Range[]) {
  let sum = 0
  for (let range of ranges) {
    sum += range.end - range.start + 1
  }
  return sum
}

export function generateRanges(fromIndex: number, groupSize: number, sourceSize: number): Range[] {
  if (sourceSize < 1) {
    throw new Error("Source size must be 1 or greater")
  }

  if (groupSize < 1) {
    throw new Error("Group size must be 1 or greater")
  }

  if (fromIndex < 0) {
    throw new Error("Starting index must not be negative")
  }

  let ranges: Range[] = []
  let currentIndex = fromIndex

  while (sumRanges(ranges) < groupSize) {

    if (ranges.length > 0) {
      let lastRange = ranges[ranges.length - 1]
      currentIndex = lastRange.end + 1
    }

    if (currentIndex > sourceSize - 1) {
      currentIndex = 0
    }

    let needToFill = groupSize - sumRanges(ranges)
    let end = Math.min(currentIndex + needToFill - 1, sourceSize - 1)
    let newRange = { start: currentIndex, end: end }
    ranges.push(newRange)
  }

  if (ranges.length === 0) {
    throw new Error("Number of ranges cannot be 0")
  }

  if (sumRanges(ranges) > groupSize) {
    throw new Error("Sum of ranges exceeded group size")
  }

  return ranges
}

export function getSubgroups(ranges: Range[], source: Upload[]): Upload[][] {
  let subgroups: Upload[][] = []
  for (let range of ranges) {
    let subgroup = source.slice(range.start, range.end + 1)
    subgroups.push(subgroup)
  }
  return subgroups
}

// export function getPlaceholderUploads(quantity: number): Upload[] {
//   let placeholders = []
//   for (let i = 0; i < quantity; i++) {
//     placeholders.push(new Upload())
//   }
//   return placeholders
// }

export function getLatestTimestampFromUploads(uploads: Upload[]) {
  let timestamps = uploads.map(u => u.createdAt)
  return Math.max(...timestamps)
}

export function selectFilePath(upload: Upload, quality: MediaQualityEnum): string {
  let file: string

  if (upload.mimeType.includes("video/")) {
    switch (quality) {
      case MediaQuality.LOW:
      case MediaQuality.MEDIUM:
        file = upload.media?.videoSmall
        break
      case MediaQuality.HIGH:
        file = upload.media?.video
        break
    }
  } else {
    switch (quality) {
      case MediaQuality.LOW:
        file = upload.media?.thumbnail
        break
      case MediaQuality.MEDIUM:
        file = upload.media?.medium
        break
      case MediaQuality.HIGH:
        file = upload.media?.original
        break
    }
  }

  if (!file) {
    throw new Error("File path could not be found")
  }

  return file
}

export function getUploadAspectRatio(uploads: Upload[]): number {
  return uploads.map(upload => upload.dimensions.aspect).reduce((a, b) => a + b) / uploads.length
}

export function calculateLayoutToFit(uploadAspectRatio: number, containerAspectRatio: number, multiplier: number = 4): { rows: number, columns: number } {
  let ratioOfRatios = uploadAspectRatio / containerAspectRatio

  // Could improve to consider the margin
  let rows = Math.max(Math.round(ratioOfRatios * multiplier), 1)
  let cols = Math.round(rows * (1 / ratioOfRatios))
  if (containerAspectRatio < 1) {
    cols = Math.round(1 / ratioOfRatios * multiplier)
    rows = Math.round(cols * ratioOfRatios)
  }

  return {
    rows: Math.ceil(rows),
    columns: Math.ceil(cols)
  }
}

export function getAutolayout(uploads: Upload[]) {
  return calculateLayoutToFit(
    getUploadAspectRatio(uploads),
    window.innerWidth / window.innerHeight
  )
}